import { useEffect, useMemo } from 'react'

import {
  IGetQRAGraphByDimensionQueryParams,
  IGetQRAGraphsQueryParams,
  IQRARiskByCategoryGraphApiResult,
  IQRARiskBySystemGraphApiResult,
  useGetQRAGraphsQuery,
  useGetQRARiskByConsequenceGraphQuery,
  useGetQRARiskByPipelineLengthGraphQuery,
  useGetQRARiskByRouteGraphQuery,
  useGetQRARiskBySystemGraphQuery,
  useGetQRARiskByThreatGraphQuery,
  useGetQRARiskForecastGraphQuery
} from 'app/services/api/qra/graphs'

import { IGraphLoadingByType, IQRAChartObjects, QRAGraphType } from './index'

export interface IUseQraGraphOverrideParams {
  graphsQueryParams: IGetQRAGraphsQueryParams
  skip: boolean
  refetchOn: unknown
}

export interface IUseQraGraphOverrideResult {
  charts?: IQRAChartObjects
  riskByPipelineLengthGraphData?: IQRARiskByCategoryGraphApiResult
  riskBySystemGraphData?: IQRARiskBySystemGraphApiResult
  isFetching: boolean
  graphLoadingByType: IGraphLoadingByType
}

const getChartsWithoutStandalonePieCharts = (charts?: IQRAChartObjects) => {
  if (charts == null) {
    return charts
  }

  const filteredCharts = { ...charts }

  delete filteredCharts[QRAGraphType.RISK_BY_LINE]
  delete filteredCharts[QRAGraphType.RISK_BY_SIGNIFICANCE_LEVEL]
  delete filteredCharts[QRAGraphType.RISK_BY_SYSTEM]

  return filteredCharts
}

export const useQraGraphOverride = ({
  graphsQueryParams,
  skip,
  refetchOn
}: IUseQraGraphOverrideParams): IUseQraGraphOverrideResult => {
  const graphByDimensionQueryParams: IGetQRAGraphByDimensionQueryParams = {
    systemScopeId: graphsQueryParams.systemScopeId,
    localFilter: graphsQueryParams.localFilter,
    riskUnit: graphsQueryParams.riskUnit,
    savedStateId1: graphsQueryParams.savedStateId1,
    savedStateId2: graphsQueryParams.savedStateId2,
    assessmentId: graphsQueryParams.assessmentId,
    continuousRouteId: graphsQueryParams.continuousRouteId
  }

  const {
    currentData: charts,
    isFetching,
    isUninitialized,
    refetch
  } = useGetQRAGraphsQuery(graphsQueryParams, {
    skip
  })

  const {
    currentData: riskByThreatGraphData,
    isFetching: isRiskByThreatFetching,
    isUninitialized: isRiskByThreatUninitialized,
    refetch: refetchRiskByThreat
  } = useGetQRARiskByThreatGraphQuery(graphByDimensionQueryParams, {
    skip
  })

  const {
    currentData: riskByConsequenceGraphData,
    isFetching: isRiskByConsequenceFetching,
    isUninitialized: isRiskByConsequenceUninitialized,
    refetch: refetchRiskByConsequence
  } = useGetQRARiskByConsequenceGraphQuery(graphByDimensionQueryParams, {
    skip
  })

  const {
    currentData: riskByRouteGraphData,
    isFetching: isRiskByRouteFetching,
    isUninitialized: isRiskByRouteUninitialized,
    refetch: refetchRiskByRoute
  } = useGetQRARiskByRouteGraphQuery(graphByDimensionQueryParams, { skip })

  const {
    currentData: riskForecastGraphData,
    isFetching: isRiskForecastFetching,
    isUninitialized: isRiskForecastUninitialized,
    refetch: refetchRiskForecast
  } = useGetQRARiskForecastGraphQuery(graphByDimensionQueryParams, {
    skip
  })

  const {
    currentData: riskByPipelineLengthGraphData,
    isFetching: isRiskByPipelineLengthFetching,
    isUninitialized: isRiskByPipelineLengthUninitialized,
    refetch: refetchRiskByPipelineLength
  } = useGetQRARiskByPipelineLengthGraphQuery(graphByDimensionQueryParams, {
    skip
  })

  const {
    currentData: riskBySystemGraphData,
    isFetching: isRiskBySystemFetching,
    isUninitialized: isRiskBySystemUninitialized,
    refetch: refetchRiskBySystem
  } = useGetQRARiskBySystemGraphQuery(graphByDimensionQueryParams, {
    skip
  })

  useEffect(() => {
    if (!isUninitialized) {
      refetch()
    }

    if (!isRiskByThreatUninitialized) {
      refetchRiskByThreat()
    }

    if (!isRiskByConsequenceUninitialized) {
      refetchRiskByConsequence()
    }

    if (!isRiskByRouteUninitialized) {
      refetchRiskByRoute()
    }

    if (!isRiskForecastUninitialized) {
      refetchRiskForecast()
    }

    if (!isRiskByPipelineLengthUninitialized) {
      refetchRiskByPipelineLength()
    }

    if (!isRiskBySystemUninitialized) {
      refetchRiskBySystem()
    }
  }, [
    refetchOn,
    isRiskByThreatUninitialized,
    isRiskByConsequenceUninitialized,
    isRiskByRouteUninitialized,
    isRiskForecastUninitialized,
    isRiskByPipelineLengthUninitialized,
    isRiskBySystemUninitialized,
    isUninitialized,
    refetch,
    refetchRiskByThreat,
    refetchRiskByConsequence,
    refetchRiskByRoute,
    refetchRiskForecast,
    refetchRiskByPipelineLength,
    refetchRiskBySystem
  ])

  const chartsWithOverride = useMemo(() => {
    const dedicatedCharts: IQRAChartObjects = {}
    const chartsWithoutStandalonePieCharts =
      getChartsWithoutStandalonePieCharts(charts)

    if (riskByThreatGraphData?.chart != null) {
      dedicatedCharts[QRAGraphType.THREATS] = riskByThreatGraphData.chart
    }

    if (riskByConsequenceGraphData?.chart != null) {
      dedicatedCharts[QRAGraphType.CONSEQUENCES] =
        riskByConsequenceGraphData.chart
    }

    if (riskByRouteGraphData?.chart != null) {
      dedicatedCharts[QRAGraphType.RISK_BY_LINE] = riskByRouteGraphData.chart
    }

    if (riskForecastGraphData?.chart != null) {
      dedicatedCharts[QRAGraphType.RISK_FORECAST] = riskForecastGraphData.chart
    }

    if (Object.keys(dedicatedCharts).length < 1) {
      return chartsWithoutStandalonePieCharts
    }

    if (chartsWithoutStandalonePieCharts == null) {
      return dedicatedCharts
    }

    return {
      ...chartsWithoutStandalonePieCharts,
      ...dedicatedCharts
    }
  }, [
    charts,
    riskByThreatGraphData,
    riskByConsequenceGraphData,
    riskByRouteGraphData,
    riskForecastGraphData
  ])

  const graphLoadingByType = useMemo(
    () => ({
      [QRAGraphType.THREATS]: isRiskByThreatFetching,
      [QRAGraphType.CONSEQUENCES]: isRiskByConsequenceFetching,
      [QRAGraphType.RISK_BY_LINE]: isRiskByRouteFetching,
      [QRAGraphType.RISK_FORECAST]: isRiskForecastFetching,
      [QRAGraphType.RISK_BY_SIGNIFICANCE_LEVEL]: isRiskByPipelineLengthFetching,
      [QRAGraphType.RISK_BY_SYSTEM]: isRiskBySystemFetching
    }),
    [
      isRiskByThreatFetching,
      isRiskByConsequenceFetching,
      isRiskByRouteFetching,
      isRiskForecastFetching,
      isRiskByPipelineLengthFetching,
      isRiskBySystemFetching
    ]
  )

  return {
    charts: chartsWithOverride,
    riskByPipelineLengthGraphData,
    riskBySystemGraphData,
    isFetching,
    graphLoadingByType
  }
}
